import * as React from 'react';
import { t } from '@reos/i18n';
import { useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSession } from '../stores/session.js';
import {
  acceptInvitation,
  fetchMe,
  getInvitation,
  signIn,
  signUp,
  type InvitationInfo,
  setActiveOrganization,
} from '../lib/api.js';

/**
 * Prijeti pozvanky (brief R1.5 bod 4). `/organization/get-invitation` a
 * `/organization/accept-invitation` v Better Auth VYZADUJI prihlasenou
 * session se shodnym e-mailem - proto se pred prihlasenim/zalozenim uctu
 * neda spolehlive overit, jestli ucet pro pozvany e-mail uz existuje.
 * ROZHODNUTI (CLAUDE.md §2, nejednoznacnost v zadani): obrazovka defaultne
 * nabidne zalozeni uctu (formular jmeno+heslo, e-mail predvyplneny z odkazu)
 * s prepinacem "uz mam ucet" na prihlaseni - po uspechu (sign-up i sign-in)
 * nasleduje standardni accept-invitation flow.
 */

type Stage =
  | { kind: 'checking' }
  | { kind: 'invalid-link' }
  | { kind: 'need-auth'; mode: 'sign-up' | 'sign-in'; error?: string }
  | { kind: 'mismatch'; sessionEmail: string }
  | { kind: 'ready'; invitation: InvitationInfo | null; error?: string }
  | { kind: 'accepting' };

function paramsFromLocation(): { id: string | null; email: string | null } {
  const params = new URLSearchParams(window.location.search);
  return { id: params.get('id'), email: params.get('email') };
}

const signUpSchema = z.object({
  name: z.string().min(1, 'Zadejte jméno'),
  password: z.string().min(12, 'Heslo musí mít alespoň 12 znaků'),
});
type SignUpForm = z.infer<typeof signUpSchema>;

const signInSchema = z.object({
  password: z.string().min(1, 'Zadejte heslo'),
});
type SignInForm = z.infer<typeof signInSchema>;

export function InvitationAcceptPage(): React.JSX.Element {
  const navigate = useNavigate();
  const setSession = useSession((s) => s.setSession);
  const [{ id, email }] = React.useState(paramsFromLocation);
  const [stage, setStage] = React.useState<Stage>({ kind: 'checking' });

  const checkSession = React.useCallback(async () => {
    if (!id || !email) {
      setStage({ kind: 'invalid-link' });
      return;
    }
    const me = await fetchMe().catch(() => null);
    if (!me) {
      setStage({ kind: 'need-auth', mode: 'sign-up' });
      return;
    }
    if (me.user.email.toLowerCase() !== email.toLowerCase()) {
      setStage({ kind: 'mismatch', sessionEmail: me.user.email });
      return;
    }
    const invitation = await getInvitation(id);
    setStage({ kind: 'ready', invitation });
  }, [id, email]);

  React.useEffect(() => {
    void checkSession();
  }, [checkSession]);

  const signUpForm = useForm<SignUpForm>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { name: '', password: '' },
  });
  const signInForm = useForm<SignInForm>({
    resolver: zodResolver(signInSchema),
    defaultValues: { password: '' },
  });

  const onSignUp = signUpForm.handleSubmit(async (data) => {
    if (!email) return;
    try {
      await signUp({ email, password: data.password, name: data.name });
      await checkSession();
    } catch {
      setStage({ kind: 'need-auth', mode: 'sign-up', error: 'Založení účtu se nezdařilo. Zkuste to prosím znovu.' });
    }
  });

  const onSignIn = signInForm.handleSubmit(async (data) => {
    if (!email) return;
    try {
      await signIn({ email, password: data.password });
      await checkSession();
    } catch {
      setStage({ kind: 'need-auth', mode: 'sign-in', error: 'Přihlášení se nezdařilo. Zkontrolujte heslo.' });
    }
  });

  const onAccept = async (): Promise<void> => {
    if (!id) return;
    const previous = stage.kind === 'ready' ? stage : { kind: 'ready' as const, invitation: null };
    setStage({ kind: 'accepting' });
    try {
      await acceptInvitation(id);
      const me = await fetchMe();
      if (me) {
        if (me.tenant !== null) await setActiveOrganization(me.tenant.id);
        setSession(me.user, me.tenant);
      }
      void navigate({ to: '/prehled' });
    } catch {
      setStage({ ...previous, error: t('webAdmin.invitation.acceptFailed', { locale: 'cs' }) });
    }
  };

  return (
    <div className="max-w-md mx-auto mt-12">
      <h1 className="text-2xl font-bold mb-2">{t('webAdmin.invitation.title', { locale: 'cs' })}</h1>

      {stage.kind === 'checking' || stage.kind === 'accepting' ? (
        <p className="text-sm text-neutral-600" data-testid="invitation-loading">{t('webAdmin.invitation.loading', { locale: 'cs' })}</p>
      ) : null}

      {stage.kind === 'invalid-link' ? (
        <p role="alert" data-testid="invitation-invalid-link" className="text-sm text-red-600">
          {t('webAdmin.invitation.invalidLink', { locale: 'cs' })}
        </p>
      ) : null}

      {stage.kind === 'mismatch' ? (
        <p role="alert" data-testid="invitation-mismatch" className="text-sm text-red-600">
          {t('webAdmin.invitation.signedInAs', { locale: 'cs' })} {stage.sessionEmail}
          {t('webAdmin.invitation.wrongRecipient', { locale: 'cs' })}{email}
          {t('webAdmin.invitation.wrongRecipientEnd', { locale: 'cs' })}
        </p>
      ) : null}

      {stage.kind === 'need-auth' ? (
        <div>
          <p className="text-sm text-neutral-600 mb-4">
            {t('webAdmin.invitation.invitedAs', { locale: 'cs' })} <strong>{email}</strong>
            {t('webAdmin.invitation.finishSignUp', { locale: 'cs' })}
          </p>
          {stage.error ? (
            <p role="alert" data-testid="invitation-auth-error" className="text-sm text-red-600 mb-4">
              {stage.error}
            </p>
          ) : null}
          {stage.mode === 'sign-up' ? (
            <form onSubmit={onSignUp} noValidate className="space-y-4" aria-label={t('webAdmin.invitation.signUpFormAriaLabel', { locale: 'cs' })}>
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">{t('webAdmin.invitation.name', { locale: 'cs' })}</label>
                <input
                  id="name"
                  autoComplete="name"
                  {...signUpForm.register('name')}
                  className="w-full border rounded px-3 py-2"
                  data-testid="invitation-signup-name"
                />
                {signUpForm.formState.errors.name ? (
                  <p role="alert" className="text-sm text-red-600 mt-1">{signUpForm.formState.errors.name.message}</p>
                ) : null}
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-1">{t('webAdmin.invitation.password', { locale: 'cs' })}</label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  {...signUpForm.register('password')}
                  className="w-full border rounded px-3 py-2"
                  data-testid="invitation-signup-password"
                />
                {signUpForm.formState.errors.password ? (
                  <p role="alert" className="text-sm text-red-600 mt-1">{signUpForm.formState.errors.password.message}</p>
                ) : null}
              </div>
              <button
                type="submit"
                disabled={signUpForm.formState.isSubmitting}
                className="w-full bg-neutral-900 text-rh-on-accent rounded px-4 py-2 hover:bg-neutral-800"
                data-testid="invitation-signup-submit"
              >
                {t('webAdmin.invitation.signUpAndAccept', { locale: 'cs' })}
              </button>
              <button
                type="button"
                onClick={() => setStage({ kind: 'need-auth', mode: 'sign-in' })}
                className="w-full text-sm underline"
                data-testid="invitation-switch-to-signin"
              >
                {t('webAdmin.invitation.haveAccount', { locale: 'cs' })}
              </button>
            </form>
          ) : (
            <form onSubmit={onSignIn} noValidate className="space-y-4" aria-label={t('webAdmin.invitation.signInFormAriaLabel', { locale: 'cs' })}>
              <div>
                <label htmlFor="signin-password" className="block text-sm font-medium mb-1">{t('webAdmin.invitation.password', { locale: 'cs' })}</label>
                <input
                  id="signin-password"
                  type="password"
                  autoComplete="current-password"
                  {...signInForm.register('password')}
                  className="w-full border rounded px-3 py-2"
                  data-testid="invitation-signin-password"
                />
                {signInForm.formState.errors.password ? (
                  <p role="alert" className="text-sm text-red-600 mt-1">{signInForm.formState.errors.password.message}</p>
                ) : null}
              </div>
              <button
                type="submit"
                disabled={signInForm.formState.isSubmitting}
                className="w-full bg-neutral-900 text-rh-on-accent rounded px-4 py-2 hover:bg-neutral-800"
                data-testid="invitation-signin-submit"
              >
                {t('webAdmin.invitation.signInAndAccept', { locale: 'cs' })}
              </button>
              <button
                type="button"
                onClick={() => setStage({ kind: 'need-auth', mode: 'sign-up' })}
                className="w-full text-sm underline"
                data-testid="invitation-switch-to-signup"
              >
                {t('webAdmin.invitation.noAccount', { locale: 'cs' })}
              </button>
            </form>
          )}
        </div>
      ) : null}

      {stage.kind === 'ready' ? (
        <div>
          <p className="text-sm text-neutral-600 mb-6" data-testid="invitation-ready-summary">
            {stage.invitation
              ? t('webAdmin.invitation.readySummary', {
                  locale: 'cs',
                  values: { org: stage.invitation.organizationName, role: stage.invitation.role },
                })
              : t('webAdmin.invitation.readySummaryFallback', { locale: 'cs' })}
          </p>
          {stage.error ? (
            <p role="alert" data-testid="invitation-accept-error" className="text-sm text-red-600 mb-4">
              {stage.error}
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => void onAccept()}
            className="w-full bg-neutral-900 text-rh-on-accent rounded px-4 py-2 hover:bg-neutral-800"
            data-testid="invitation-accept-submit"
          >
            {t('webAdmin.invitation.accept', { locale: 'cs' })}
          </button>
        </div>
      ) : null}
    </div>
  );
}
